import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  TrendingUp, 
  BarChart3, 
  Moon,
  Target,
  AlertTriangle,
  ArrowRight,
  ChevronDown,
  Bot,
  Trophy,
  Calendar,
  Star,
  Check,
  Sparkles
} from "lucide-react";

// App Logo URL
const APP_LOGO = "https://customer-assets.emergentagent.com/job_trend-stock-app-1/artifacts/tmh7qekk_ChatGPT%20Image%20Feb%2025%2C%202026%2C%2001_23_15%20PM.png";

// Animation variants
const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Core Features
const coreFeatures = [
  {
    icon: BarChart3,
    title: "Technical Signals",
    color: "from-cyan-500 to-blue-500",
    items: [
      "EMA trend alignment",
      "RSI & momentum structure",
      "Volatility expansion & compression",
      "Volume acceleration detection"
    ]
  },
  {
    icon: Moon,
    title: "Daily Cosmic Forecast",
    color: "from-amber-500 to-orange-500",
    items: [
      "Bullish/Bearish market score",
      "Lunar phase indicators (Full Moon, New Moon)",
      "Planetary aspects (Sun-Pluto, Saturn-Neptune)",
      "Cosmic alignment strength meter"
    ]
  },
  {
    icon: Calendar,
    title: "Astrological Cycles",
    color: "from-purple-500 to-pink-500",
    items: [
      "Mercury retrograde flags",
      "Jupiter growth cycles",
      "Saturn constraint markers",
      "Rahu/Ketu axis activation"
    ]
  }
];

// New Features Highlights
const highlightFeatures = [
  {
    icon: Trophy,
    title: "CycleFusion Top 5",
    description: "Track and monitor the performance of our top 5 daily stock picks across all trading strategies with real-time updates."
  },
  {
    icon: Bot,
    title: "Scott - AI Assistant",
    description: "Your personal astrology + market guide. A cowboy-inspired AI assistant to answer questions about cosmic market timing and strategies."
  },
  {
    icon: Calendar,
    title: "Daily Cosmic Forecast",
    description: "Start each trading day with personalized astrological insights, planetary alignments, and market timing recommendations."
  }
];

// Trading Opportunity Types
const opportunityTypes = [
  { color: "bg-red-500", label: "Day Trading Opportunities" },
  { color: "bg-orange-500", label: "Swing Trading Setups" },
  { color: "bg-emerald-500", label: "Long-Term Positioning Candidates" }
];

// Pricing Plans
const pricingPlans = [
  {
    name: "Stock Trader Lite",
    price: "Free",
    period: "",
    description: "Get started with essential features",
    features: [
      "Basic technical signals",
      "Limited astrological insights",
      "Daily market overview",
      "CycleFusion Top 5 (view only)"
    ],
    popular: false,
    cta: "Get Started"
  },
  {
    name: "Stock Trader Elite",
    price: "$9.99",
    period: "/mo",
    yearlyPrice: "$99.99/yr",
    yearlySavings: "save $20/yr",
    description: "Full access to elite trading signals & cosmic insights",
    features: [
      "Unlimited Stock Analyses",
      "Market Overview (Advanced)",
      "Daily Cosmic Forecast",
      "CycleFusion Top 5 (All Strategies)",
      "News Sentiment (Real-time, Political 2x)",
      "AI Explanations (Detailed)",
      "Full Astrology Insights",
      "Quantitative Astrology Engine"
    ],
    popular: true,
    cta: "Subscribe Now"
  }
];

// Navbar Component
const Navbar = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 bg-[#030712]/80 backdrop-blur-xl border-b border-white/5">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img 
            src={APP_LOGO} 
            alt="StockTrendFusion" 
            className="w-10 h-10 rounded-xl"
          />
          <span className="font-semibold text-lg text-white">StockTrendFusion</span>
        </Link>
        <div className="flex items-center gap-6">
          <Link 
            to="/support" 
            className="text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid="nav-support-link"
          >
            Support
          </Link>
          <a
            href="#pricing"
            className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white text-sm font-medium transition-all btn-lift shadow-lg shadow-cyan-500/25"
            data-testid="nav-pricing-btn"
          >
            View Plans
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  </nav>
);

// Hero Section
const HeroSection = () => (
  <section className="relative min-h-screen flex items-center pt-16 overflow-hidden" data-testid="hero-section">
    {/* Abstract Background */}
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-[#030712]" />
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[120px]" />
      <div className="absolute top-1/3 right-1/3 w-[400px] h-[400px] bg-amber-500/5 rounded-full blur-[100px]" />
      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
    </div>
    
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 relative z-10">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Text Content */}
        <motion.div 
          className="hero-text"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.div variants={fadeUp} className="flex items-center gap-2 mb-6">
            <div className="h-px w-12 bg-gradient-to-r from-cyan-500 to-transparent" />
            <span className="text-sm font-medium tracking-wider uppercase text-cyan-400">
              Next-Gen Market Intelligence
            </span>
          </motion.div>
          
          <motion.h1 
            variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-white mb-6"
          >
            Data-Driven
            <span className="block bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
              Stock Intelligence
            </span>
          </motion.h1>
          
          <motion.p 
            variants={fadeUp}
            className="text-base md:text-lg leading-relaxed text-zinc-400 mb-8 max-w-xl"
          >
            StockTrendFusion identifies high-probability stock opportunities by fusing technical signals 
            and astrological cycles into a unified scoring system.
          </motion.p>
          
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
            <a
              href="#pricing"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-medium transition-all btn-lift shadow-lg shadow-cyan-500/25"
              data-testid="hero-cta-btn"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        </motion.div>
        
        {/* App Logo/Icon */}
        <motion.div 
          className="relative flex justify-center lg:justify-end"
          initial={{ opacity: 0, scale: 0.9, rotate: -5 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="relative">
            {/* Glow effects */}
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/30 via-purple-500/20 to-amber-500/30 rounded-[40px] blur-[60px] scale-110" />
            <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 to-pink-500/20 rounded-[40px] blur-[40px] scale-105" />
            
            <img 
              src={APP_LOGO}
              alt="StockTrendFusion App"
              className="relative w-[280px] md:w-[340px] h-auto rounded-[32px] shadow-2xl"
              data-testid="hero-app-icon"
            />
            
            {/* Floating indicators */}
            <motion.div 
              className="absolute -top-4 -right-4 px-3 py-1.5 bg-red-500/90 rounded-full text-white text-xs font-medium backdrop-blur-sm"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Day Trade
            </motion.div>
            <motion.div 
              className="absolute -bottom-2 -left-4 px-3 py-1.5 bg-emerald-500/90 rounded-full text-white text-xs font-medium backdrop-blur-sm"
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2.5, repeat: Infinity }}
            >
              Long-Term
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
    
    {/* Scroll indicator */}
    <motion.div 
      className="absolute bottom-8 left-1/2 -translate-x-1/2"
      animate={{ y: [0, 8, 0] }}
      transition={{ duration: 1.5, repeat: Infinity }}
    >
      <ChevronDown className="w-6 h-6 text-zinc-500" />
    </motion.div>
  </section>
);

// New Features Highlight Section
const FeaturesHighlightSection = () => (
  <section className="py-24 md:py-32 relative" data-testid="features-highlight-section">
    <div className="absolute inset-0 bg-gradient-to-b from-[#030712] via-[#0a0f1a] to-[#030712]" />
    
    <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
      <motion.div 
        className="text-center mb-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6">
          <Sparkles className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-medium text-amber-400">What's New</span>
        </div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
          Powerful New Features
        </h2>
        <p className="text-zinc-400 max-w-2xl mx-auto">
          Experience the next level of market intelligence with our latest updates
        </p>
      </motion.div>
      
      <div className="grid md:grid-cols-3 gap-8">
        {highlightFeatures.map((feature, index) => (
          <motion.div
            key={feature.title}
            className="relative group text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.15 }}
            data-testid={`highlight-feature-${index}`}
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
              <feature.icon className="w-8 h-8 text-cyan-400" strokeWidth={1.5} />
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
            <p className="text-zinc-400 leading-relaxed">{feature.description}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// Core Analysis Section
const CoreAnalysisSection = () => (
  <section className="py-24 md:py-32 relative" data-testid="core-analysis-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
      <motion.div 
        className="text-center mb-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <p className="text-sm font-medium tracking-wider uppercase text-cyan-400 mb-4">
          Fusion Intelligence
        </p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
          Multi-Dimensional Analysis
        </h2>
        <p className="text-zinc-400 max-w-2xl mx-auto">
          Each timing marker is converted into quantifiable features and integrated into a structured scoring system
        </p>
      </motion.div>
      
      <div className="grid md:grid-cols-3 gap-6">
        {coreFeatures.map((category, index) => (
          <motion.div
            key={category.title}
            className="relative group"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.15 }}
            data-testid={`category-card-${index}`}
          >
            {/* Card glow on hover */}
            <div className={`absolute inset-0 bg-gradient-to-br ${category.color} rounded-2xl opacity-0 group-hover:opacity-10 blur-xl transition-opacity duration-500`} />
            
            <div className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-8 h-full hover:border-white/20 transition-colors duration-300">
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center mb-6 shadow-lg`}>
                <category.icon className="w-7 h-7 text-white" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">{category.title}</h3>
              <ul className="space-y-3">
                {category.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-zinc-400 text-sm">
                    <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${category.color} mt-2 shrink-0`} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// Daily Cosmic Forecast Preview Section
const CosmicForecastSection = () => (
  <section className="py-24 md:py-32 relative overflow-hidden" data-testid="cosmic-forecast-section">
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0f1a] to-transparent" />
    
    <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          className="order-2 lg:order-1"
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {/* Cosmic Forecast Card Mockup */}
          <div className="relative rounded-2xl border border-white/10 bg-[#0f172a]/80 backdrop-blur-sm p-6 max-w-md mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">🪐</span>
              <h3 className="text-xl font-semibold text-white">Daily Cosmic Forecast</h3>
              <span className="text-sm text-zinc-500 ml-auto">2026-05-01</span>
            </div>
            
            <div className="flex items-center justify-between mb-4">
              <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-sm font-medium border border-emerald-500/30">
                Very Bullish
              </span>
              <span className="text-zinc-400">Score: <span className="text-white font-mono">0.57</span></span>
            </div>
            
            <p className="text-zinc-400 text-sm mb-6">
              Strong positive cosmic alignment favoring market gains
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                <span className="text-2xl">🌕</span>
                <div className="flex-1">
                  <p className="text-white font-medium">Full Moon</p>
                  <p className="text-amber-400 text-sm">Peak emotions, high volatility</p>
                </div>
                <span className="text-zinc-400 text-sm">100%</span>
              </div>
              
              <div className="text-sm text-zinc-400 space-y-1 mt-4">
                <p><span className="text-amber-400">🌕</span> Full Moon: Peak emotions, high volatility</p>
                <p>Sun-Pluto □: challenging energy</p>
                <p>Saturn-Neptune ♂: intensifies energy</p>
              </div>
            </div>
          </div>
        </motion.div>
        
        <motion.div
          className="order-1 lg:order-2"
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6">
            <Moon className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-medium text-amber-400">Daily Insights</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Daily Cosmic <span className="text-amber-400">Forecast</span>
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-zinc-400 mb-8">
            Start each trading day with a comprehensive cosmic outlook. Our forecast analyzes planetary positions, lunar phases, and astrological aspects to give you a market sentiment score.
          </p>
          
          <div className="space-y-4">
            {[
              "Bullish/Bearish sentiment indicator with score",
              "Lunar phase impact on market volatility",
              "Planetary aspects and their market influence",
              "Updated daily before market open"
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 text-zinc-300">
                <div className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-amber-400" />
                </div>
                {item}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Scott AI Section
const ScottAISection = () => (
  <section className="py-24 md:py-32 relative overflow-hidden" data-testid="scott-ai-section">
    <div className="absolute inset-0">
      <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[100px] -translate-y-1/2" />
      <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[100px] -translate-y-1/2" />
    </div>
    
    <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-6">
            <span className="text-lg">🤠</span>
            <span className="text-sm font-medium text-purple-400">Astrology + Markets Guide</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Meet <span className="text-purple-400">Scott</span>
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-zinc-400 mb-8">
            Ask Scott anything about the app's astrology signals, how news moves markets, or how to read a prediction.
          </p>
          
          <div className="space-y-3">
            <p className="text-sm text-zinc-500 mb-2">Popular questions:</p>
            {[
              "What does Mercury retrograde mean for stocks?",
              "How do I read the quant astrology score?",
              "Why is political news 2x weighted?",
              "What do the ML class probabilities mean?"
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 text-zinc-300 text-sm bg-white/5 rounded-lg px-4 py-2 border border-white/10">
                {item}
              </div>
            ))}
          </div>
        </motion.div>
        
        <motion.div
          className="relative"
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {/* Chat mockup */}
          <div className="relative rounded-2xl border border-white/10 bg-[#0f172a]/80 backdrop-blur-sm p-6 max-w-md mx-auto">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <span className="text-3xl">🤠</span>
                <div>
                  <p className="font-medium text-white">Scott</p>
                  <p className="text-xs text-zinc-400">Astrology + markets guide</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-start">
                <div className="bg-white/5 rounded-2xl rounded-tl-sm px-4 py-3 border border-white/10">
                  <p className="text-sm text-zinc-300 leading-relaxed">
                    Hey, I'm Scott — your StockTrendFusion guide. Ask me anything about the app's astrology signals, how news moves markets, or how to read a prediction. I stay strictly on-topic — everything else I'll politely nudge back to the signals.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 pt-4 border-t border-white/10">
              <div className="flex items-center gap-2 bg-white/5 rounded-xl px-4 py-3 border border-white/10">
                <span className="text-zinc-500 text-sm">Ask Scott about signals, news or picks...</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Pricing Section
const PricingSection = () => (
  <section id="pricing" className="py-24 md:py-32 relative" data-testid="pricing-section">
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0f1a] to-transparent" />
    
    <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
      <motion.div 
        className="text-center mb-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <p className="text-sm font-medium tracking-wider uppercase text-cyan-400 mb-4">
          Pricing Plans
        </p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
          Choose Your Plan
        </h2>
        <p className="text-zinc-400 max-w-2xl mx-auto">
          Start free or unlock the full power of StockTrendFusion with Elite
        </p>
      </motion.div>
      
      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {pricingPlans.map((plan, index) => (
          <motion.div
            key={plan.name}
            className={`relative rounded-2xl border ${plan.popular ? 'border-cyan-500/50' : 'border-white/10'} bg-white/5 backdrop-blur-sm p-8`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.15 }}
            data-testid={`pricing-card-${index}`}
          >
            {plan.popular && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-medium">
                Most Popular
              </div>
            )}
            
            <div className="text-center mb-8">
              <h3 className="text-xl font-semibold text-white mb-2">{plan.name}</h3>
              <p className="text-zinc-400 text-sm mb-4">{plan.description}</p>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-4xl font-bold text-white">{plan.price}</span>
                {plan.period && <span className="text-zinc-400">{plan.period}</span>}
              </div>
              {plan.yearlyPrice && (
                <p className="text-sm text-cyan-400 mt-2">
                  or {plan.yearlyPrice} <span className="text-zinc-500">($8.33/mo — {plan.yearlySavings})</span>
                </p>
              )}
            </div>
            
            <ul className="space-y-4 mb-8">
              {plan.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-3 text-zinc-300">
                  <Check className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
                  {feature}
                </li>
              ))}
            </ul>
            
            <button
              className={`w-full py-3 rounded-xl font-medium transition-all ${
                plan.popular
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white shadow-lg shadow-cyan-500/25'
                  : 'bg-white/10 hover:bg-white/20 text-white'
              }`}
              data-testid={`pricing-cta-${index}`}
            >
              {plan.cta}
            </button>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// Disclaimer Section
const DisclaimerSection = () => (
  <section className="py-16 relative" data-testid="disclaimer-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <motion.div
        className="rounded-2xl border border-amber-500/30 bg-amber-500/5 backdrop-blur-sm p-8"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-6 h-6 text-amber-400" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-amber-400 mb-2">Important Disclaimer</h3>
            <p className="text-zinc-400 leading-relaxed">
              StockTrendFusion is intended for <span className="text-white">research and educational purposes only</span>. 
              It does not provide financial advice. Astrological and timing markers are experimental and may not have predictive value. 
              Investing involves risk. Past performance is not indicative of future results. Always consult with qualified financial 
              professionals before making investment decisions.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="py-24 md:py-32" data-testid="cta-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <motion.div 
        className="relative rounded-3xl overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#0f172a] to-[#1a0a28]" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/20 rounded-full blur-[120px]" />
        
        {/* Border */}
        <div className="absolute inset-0 rounded-3xl border border-white/10" />
        
        <div className="relative z-10 p-12 md:p-20 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <img 
              src={APP_LOGO} 
              alt="StockTrendFusion" 
              className="w-24 h-24 mx-auto rounded-2xl shadow-2xl"
            />
          </motion.div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Start Your Analysis Journey
          </h2>
          <p className="text-base md:text-lg text-zinc-400 mb-10 max-w-2xl mx-auto">
            Experience the fusion of technical analysis and astrological timing in one powerful platform. Start free today.
          </p>
          
          <a
            href="#pricing"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-medium transition-all btn-lift shadow-lg shadow-cyan-500/25"
            data-testid="cta-btn"
          >
            Get Started Free
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="py-12 border-t border-white/5" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <img 
            src={APP_LOGO} 
            alt="StockTrendFusion" 
            className="w-10 h-10 rounded-xl"
          />
          <span className="font-semibold text-white">StockTrendFusion</span>
        </div>
        
        <div className="flex items-center gap-8">
          <Link 
            to="/support" 
            className="text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid="footer-support-link"
          >
            Support
          </Link>
          <Link 
            to="/privacy" 
            className="text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid="footer-privacy-link"
          >
            Privacy Policy
          </Link>
          <Link 
            to="/terms" 
            className="text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid="footer-terms-link"
          >
            Terms of Service
          </Link>
        </div>
        
        <p className="text-sm text-zinc-500">
          &copy; {new Date().getFullYear()} StockTrendFusion. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// Main Landing Page Component
const LandingPage = () => {
  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Navbar />
      <HeroSection />
      <FeaturesHighlightSection />
      <CoreAnalysisSection />
      <CosmicForecastSection />
      <ScottAISection />
      <PricingSection />
      <DisclaimerSection />
      <CTASection />
      <Footer />
    </div>
  );
};

export default LandingPage;
